export const PROOF_ATTRIBUTION_VERSION = '1';

export const PROOF_ORIGIN_SURFACES = Object.freeze({
  APR: 'apr',
  PROOF_RICH_SHARE: 'proof_rich_share',
  PROOF_PORTFOLIO: 'proof_portfolio',
  PROFILE_PROOF_PORTFOLIO: 'profile_proof_portfolio',
});

const VALID_PROOF_ORIGIN_SURFACES = new Set(
  Object.values(PROOF_ORIGIN_SURFACES),
);

const VALID_PROOF_SOURCES = new Set([
  'completion_proof_share',
  'proof_portfolio',
]);

export const PROOF_ATTRIBUTION_QUERY_KEYS = Object.freeze([
  'proofId',
  'refPlayerId',
  'source',
  'originSurface',
  'attributionVersion',
  'regionSlug',
]);

export const cleanProofAttributionValue = (value, max = 160) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return '';
  return String(value).replace(/[\r\n\t]+/g, ' ').trim().slice(0, max);
};

export const isInternalObjectIdText = (value) =>
  /^[a-f0-9]{24}$/i.test(String(value || '').trim());

export const normalizePublicIdentityText = (value) => {
  const clean = cleanProofAttributionValue(value, 80).replace(/\s+/g, ' ');
  if (!clean || isInternalObjectIdText(clean)) return '';
  if (/^[^@\s]+@[^@\s]+$/.test(clean)) return '';
  return clean;
};

export const normalizeProofOriginSurface = (value, fallback = '') => {
  const clean = cleanProofAttributionValue(value, 80);
  if (VALID_PROOF_ORIGIN_SURFACES.has(clean)) return clean;
  return VALID_PROOF_ORIGIN_SURFACES.has(fallback) ? fallback : '';
};

export const isSafePublicPlayerId = (value) => {
  const clean = normalizePublicIdentityText(value).replace(/^@+/, '');
  if (!clean || isInternalObjectIdText(clean)) return false;
  return /^[A-Za-z0-9_-]{3,40}$/.test(clean);
};

export const normalizeProofRefPlayerId = (value) => {
  const clean = normalizePublicIdentityText(value).replace(/^@+/, '');
  return isSafePublicPlayerId(clean) ? clean : '';
};

export const normalizeProofSource = (value) => {
  const clean = cleanProofAttributionValue(value, 120);
  return VALID_PROOF_SOURCES.has(clean) ? clean : '';
};

export const normalizeProofId = (value) =>
  cleanProofAttributionValue(value, 120);

export const normalizeProofRegionSlug = (value) =>
  cleanProofAttributionValue(value, 120);

export const sanitizeProofAttributionHint = (input = {}) => {
  let source = input;
  if (typeof URLSearchParams !== 'undefined' && input instanceof URLSearchParams) {
    source = Object.fromEntries(input.entries());
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null;

  const proofId = normalizeProofId(source.proofId);
  if (!proofId) return null;

  const hint = {
    attributionVersion: Number(PROOF_ATTRIBUTION_VERSION),
    proofId,
  };

  const refPlayerId = normalizeProofRefPlayerId(source.refPlayerId);
  if (refPlayerId) hint.refPlayerId = refPlayerId;

  const normalizedSource = normalizeProofSource(source.source);
  if (normalizedSource) hint.source = normalizedSource;

  const originSurface = normalizeProofOriginSurface(source.originSurface);
  if (originSurface) hint.originSurface = originSurface;

  const regionSlug = normalizeProofRegionSlug(source.regionSlug);
  if (regionSlug) hint.regionSlug = regionSlug;

  return hint;
};

export const readProofAttributionHintFromSearch = (search = '') => {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  return sanitizeProofAttributionHint(params);
};

export const attachProofAttributionHintToPendingCreate = (payload, hint) => {
  const sanitized = sanitizeProofAttributionHint(hint);
  if (!sanitized) return payload;
  return {
    ...(payload || {}),
    proofAttributionHint: sanitized,
  };
};

export const getProofAttributionRefPlayerId = (
  proof,
  fallbackRefPlayerId = '',
) =>
  [
    proof?.proofAttribution?.refPlayerId,
    proof?.attribution?.refPlayerId,
    proof?.helper?.playerId,
    fallbackRefPlayerId,
  ]
    .map(normalizeProofRefPlayerId)
    .find(Boolean) || '';

export const buildProofAttributedPostTaskUrl = ({
  proofId,
  refPlayerId,
  source,
  originSurface,
  regionSlug,
  extraParams = {},
} = {}) => {
  const params = new URLSearchParams();
  params.set('intent', 'post_task');

  const safeSource = normalizeProofSource(source);
  if (safeSource) params.set('source', safeSource);

  const safeProofId = normalizeProofId(proofId);
  if (safeProofId) params.set('proofId', safeProofId);

  const safeRefPlayerId = normalizeProofRefPlayerId(refPlayerId);
  if (safeRefPlayerId) params.set('refPlayerId', safeRefPlayerId);

  const safeOriginSurface = normalizeProofOriginSurface(
    originSurface,
    PROOF_ORIGIN_SURFACES.PROOF_PORTFOLIO,
  );
  if (safeOriginSurface) params.set('originSurface', safeOriginSurface);
  params.set('attributionVersion', PROOF_ATTRIBUTION_VERSION);

  const safeRegionSlug = normalizeProofRegionSlug(regionSlug);
  if (safeRegionSlug) params.set('regionSlug', safeRegionSlug);

  Object.entries(extraParams || {}).forEach(([key, value]) => {
    const safeKey = cleanProofAttributionValue(key, 80);
    const safeValue = cleanProofAttributionValue(value, 512);
    if (!safeKey || !safeValue) return;
    params.set(safeKey, safeValue);
  });

  return `/playermap?${params.toString()}`;
};
